import React, { Fragment, useMemo } from 'react'
import { useSelector } from 'react-redux'

import PageSection from '../../../../features/components/PageSection'
import RightsListTableData from './RightsListTableData'
import SkeletonListRow from '../../../../features/components/List/Skeleton'

import useIsAdvancedPartnership from '../../../../features/hooks/useIsAdvancedPartnership'

import { getGroupedRightsList } from './helpers'
import { insertIf } from '../../../../helpers/common'

import {
  rightsListErrorSelector,
  rightsListIsLoadingSelector,
  rightsListSelector,
  rightsListWasLoadedSelector
} from '../../../../modules/selectors/rights'
import { partnershipsWasLoadedSelector } from '../../../../modules/selectors/partnerships'
import { partnershipAccountTypeSelector } from '../../../../modules/selectors/app'

import { RIGHTS_HOLDER_TYPE } from '../../../../constants/selfAccounts'

import useStyles from '../styles'

const RightsListTable = () => {
  const classes = useStyles()

  const rightsList = useSelector(rightsListSelector)
  const rightsListIsLoading = useSelector(rightsListIsLoadingSelector)
  const rightsListWasLoaded = useSelector(rightsListWasLoadedSelector)
  const rightsListError = useSelector(rightsListErrorSelector)
  const partnershipsWasLoaded = useSelector(partnershipsWasLoadedSelector)
  const partnershipAccountType = useSelector(partnershipAccountTypeSelector)

  const groupedRightsList = useMemo(() => {
    return getGroupedRightsList(rightsList)
  }, [rightsList])

  // Convert the grouped object back into an array and sort the rights in each category
  const sortedGroupedRightsList = Object.values(groupedRightsList).map(category => {
    // Sort the rights in each category by right order
    category.rights.sort((a, b) => a.order - b.order)

    // Return an object with category and rights properties
    return {
      category: category.category,
      rights: category.rights
    }
  })

  // show category section if more then 1 groups available
  // no title if it’s the only section, or ‘Other’ if there are at least one other category
  const showTitle = sortedGroupedRightsList.length > 1

  const isAdvancedPartnership = useIsAdvancedPartnership()

  const partnershipIsEmptyText = useMemo(
    () =>
      partnershipAccountType === RIGHTS_HOLDER_TYPE
        ? 'This partnership is currently empty. To get started, please create a new right.'
        : 'This partnership is currently empty.',
    [partnershipAccountType]
  )

  const tableColumnsSize = useMemo(() => {
    return {
      name: isAdvancedPartnership ? 400 : 280,
      partnership: 220, // currently hidden
      allocation: 120,
      delivered: 140,
      status: 120,
      actions: 310,
      file: isAdvancedPartnership ? 35 : 75
    }
  }, [isAdvancedPartnership])

  const rightsListColumnsSchema = useMemo(
    () => [
      {
        style: { minWidth: tableColumnsSize.name }
      },
      {
        style: { minWidth: tableColumnsSize.allocation }
      },
      {
        style: { minWidth: tableColumnsSize.delivered }
      },
      {
        style: { minWidth: tableColumnsSize.status }
      },
      ...insertIf(!isAdvancedPartnership, {
        style: { minWidth: tableColumnsSize.actions }
      }),
      {
        style: { maxWidth: tableColumnsSize.file }
      }
    ],
    [isAdvancedPartnership, tableColumnsSize]
  )

  const itemsIsEmpty = rightsList.length
  // request done, but data is empty or received error
  const showNoDataMessage =
    itemsIsEmpty && (rightsListWasLoaded || rightsListError) && !rightsListIsLoading && !partnershipsWasLoaded

  if (showNoDataMessage) {
    return <p className={classes.noDataMessage}>{partnershipIsEmptyText}</p>
  }

  if (rightsListIsLoading) {
    return (
      <PageSection>
        {/* don't change this class name, it's used in PDF generation*/}
        <ul className={classes.rightsList}>
          {Array(3)
            .fill(Math.random())
            .map((item, index) => (
              <SkeletonListRow key={index} cols={rightsListColumnsSchema} />
            ))}
        </ul>
      </PageSection>
    )
  }

  return (
    <>
      {sortedGroupedRightsList.map(categoryGroup => (
        <Fragment key={categoryGroup.category.id}>
          {showTitle && <h2 className={classes.categoryHeadline}>{categoryGroup.category.name}</h2>}
          <PageSection>
            {/* don't change this class name, it's used in PDF generation*/}
            <ul className={classes.rightsList}>
              <RightsListTableData rightsList={categoryGroup.rights} tableColumnsSize={tableColumnsSize} />
            </ul>
          </PageSection>
        </Fragment>
      ))}
    </>
  )
}

export default RightsListTable
